import React, { useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { useInjection } from "@presentation/components/dependencyInjection";
import { ProjectManagementService } from "@application/useCases/workspace/ProjectManagementService";
import { extractProblemDetails } from "@presentation/common/ErrorHelpers";
import { useNotifications } from "@presentation/components/notifications";
import { describeManagerInvariantDenial } from "@presentation/common/utils/managerInvariantDenial";
import { formatUserDisplayName, useUserDisplayNames } from "@presentation/hooks/useUserDisplayNames";
import type { ProjectDetail } from "@domain/schemas/workspace";
import { useWorkspaceSuccessNotification } from "@presentation/components/notifications/useWorkspaceSuccessNotification";

interface RemoveProjectMemberDialogProps {
  projectId: string;
  userId: string;
  onClose: () => void;
  onSuccess: (updated: ProjectDetail) => void;
}

/**
A project is the leaf level - unlike team removal, there is nothing beneath it to cascade into, so
this is a plain confirm with no checkbox.
*/
export const RemoveProjectMemberDialog: React.FC<RemoveProjectMemberDialogProps> = ({ projectId, userId, onClose, onSuccess }) => {
  const projectManagementService = useInjection(ProjectManagementService);
  const userNames = useUserDisplayNames(React.useMemo(() => [userId], [userId]));
  const { pushNotification } = useNotifications();
  const notifySuccess = useWorkspaceSuccessNotification();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      const updated = await projectManagementService.removeProjectMemberAsync(projectId, userId);
      notifySuccess("project", "memberRemoved");
      onSuccess(updated);
      onClose();
    } catch (error) {
      pushNotification(
        describeManagerInvariantDenial(error, "project") ?? extractProblemDetails(error).detail ?? extractProblemDetails(error).title ?? "Could not remove this member.",
        "error",
        10_000,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog title="Remove member" onClose={onClose} width={420}>
      <div style={{ display: "grid", gap: 16 }}>
        <p style={{ margin: 0 }}>
          Remove <strong>{formatUserDisplayName(userNames[userId], userId)}</strong> from this project?
        </p>
      </div>
      <DialogActionsBar>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button themeColor="primary" disabled={isSubmitting} onClick={handleConfirm}>
          Remove
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
};
